<script setup>
  import { storeToRefs } from "pinia";
  import { Trash, Plus } from "lucide-vue-next";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { Select } from "@/components/episto-ui/select";
  import {
    Select as SelectPrimitive,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
  } from "@/components/ui/select";
  import { useSurveyLearningsStore } from "@stores/survey/surveyLearnings";

  const { learnings } = storeToRefs(useSurveyLearningsStore());

  const costTypeOptions = [
    { label: "Panel", value: "Panel" },
    { label: "Incentives", value: "Incentives" },
    { label: "Questionnaire design", value: "Questionnaire design" },
    { label: "Translation", value: "Translation" },
    { label: "Codification", value: "Codification" },
    { label: "Analysis", value: "Analysis" },
  ];
  const issueTypeOptions = [
    {
      label: "QUESTIONNAIRE",
      options: [
        {
          value: "LOI is high (but in line with expectations)",
          label: "LOI is high (but in line with expectations)",
        },
        {
          value: "LOI is higher than expected",
          label: "LOI is higher than expected",
        },
        {
          value: "Topic is not engaging",
          label: "Topic is not engaging",
        },
        {
          value: "Some questions cause high dropouts",
          label: "Some questions cause high dropouts",
        },
        {
          value: "External questionnaire",
          label: "External questionnaire",
        },
        {
          value: "Other questionnaire issue",
          label: "Other questionnaire issue",
        },
      ],
    },
    {
      label: "TARGET",
      options: [
        {
          value: "Country",
          label: "Country",
        },
        {
          value: "Target is hard to reach",
          label: "Target is hard to reach",
        },
        {
          value: "Target is harder than expected due to incidence estimation issue",
          label: "Target is harder than expected due to incidence estimation issue",
        },
        {
          value: "Target is harder than expected due to issues with target definition",
          label: "Target is harder than expected due to issues with target definition",
        },
        {
          value: "Other target issue",
          label: "Other target issue",
        },
      ],
    },
    {
      label: "QUOTAS",
      options: [
        {
          value: "Quotas are hard",
          label: "Quotas are hard",
        },
        {
          value: "Quotas are harder than expected",
          label: "Quotas are harder than expected",
        },
        {
          value: "Quotas are not in line with the topic",
          label: "Quotas are not in line with the topic",
        },
        {
          value: "Other quota issue",
          label: "Other quota issue",
        },
      ],
    },
    {
      label: "OTHER",
      options: [
        {
          value: "Internal issues (setup, bugs...)",
          label: "Internal issues (setup, bugs...)",
        },
        {
          value: "Pricing error",
          label: "Pricing error",
        },
        {
          value: "Pricing discount",
          label: "Pricing discount",
        },
        {
          value: "Meta related issues",
          label: "Meta related issues",
        },
        {
          value: "Other issue",
          label: "Other issue",
        },
      ],
    },
  ];

  const addCost = () => {
    learnings.value.learnings_costs.push({});
  };
  const addIssue = () => {
    learnings.value.learnings_issues.push({});
  };

  const removeCost = (index) => {
    learnings.value.learnings_costs.splice(index, 1);
  };
  const removeIssue = (index) => {
    learnings.value.learnings_issues.splice(index, 1);
  };
</script>

<template>
  <div
    data-cy="survey-learnings-page"
    class="flex justify-center px-4">
    <div class="w-full max-w-3xl">
      <Card>
        <CardHeader class="px-6 pt-6 pb-4">
          <CardTitle>Survey Learnings</CardTitle>
        </CardHeader>
        <CardContent class="px-6 pb-6">
          <Accordion
            type="multiple"
            :default-value="['learningsCost', 'learningsIssues', 'learningsComments']">
            <!-- Costs section -->
            <AccordionItem value="learningsCost">
              <AccordionTrigger>Costs</AccordionTrigger>
              <AccordionContent>
                <div class="space-y-3">
                  <!-- Header row -->
                  <div class="grid grid-cols-[1fr_160px_36px] gap-2">
                    <span class="text-xs font-medium text-muted-foreground">Cost type</span>
                    <span class="text-xs font-medium text-muted-foreground">Cost</span>
                    <span />
                  </div>

                  <!-- Cost rows -->
                  <div
                    v-for="(cost, index) in learnings.learnings_costs"
                    :key="`learnings-cost-item-${index}`"
                    class="space-y-2">
                    <div class="grid grid-cols-[1fr_160px_36px] items-center gap-2">
                      <Select
                        v-model="cost.cost_type"
                        :options="costTypeOptions"
                        placeholder="Select" />
                      <Input
                        v-model.number="cost.cost"
                        type="number"
                        :min="0" />
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        @click="removeCost(index)">
                        <Trash class="size-4" />
                      </Button>
                    </div>
                    <Textarea
                      v-model="cost.cost_comment"
                      :rows="2"
                      placeholder="Add comment..." />
                  </div>

                  <Button
                    variant="outline"
                    @click="addCost()">
                    <Plus class="size-4" />
                    Add cost
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Performance issues section -->
            <AccordionItem value="learningsIssues">
              <AccordionTrigger>Performance issues</AccordionTrigger>
              <AccordionContent>
                <div class="space-y-3">
                  <div
                    v-for="(issue, index) in learnings.learnings_issues"
                    :key="`learnings-issues-item-${index}`"
                    class="space-y-2">
                    <div class="flex items-center gap-2">
                      <SelectPrimitive
                        v-model="issue.issue_type"
                        class="flex-1">
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup
                            v-for="group in issueTypeOptions"
                            :key="group.label">
                            <SelectLabel>{{ group.label }}</SelectLabel>
                            <SelectItem
                              v-for="item in group.options"
                              :key="item.value"
                              :value="item.value">
                              {{ item.label }}
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </SelectPrimitive>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        @click="removeIssue(index)">
                        <Trash class="size-4" />
                      </Button>
                    </div>
                    <Textarea
                      v-model="issue.issue_comment"
                      :rows="2"
                      placeholder="Add comment..." />
                  </div>

                  <Button
                    variant="outline"
                    @click="addIssue()">
                    <Plus class="size-4" />
                    Add issue
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Other comments section -->
            <AccordionItem value="learningsComments">
              <AccordionTrigger>Other comments</AccordionTrigger>
              <AccordionContent>
                <div class="space-y-4">
                  <div class="space-y-2">
                    <label class="text-xs font-medium text-muted-foreground">CSM</label>
                    <Textarea
                      v-model="learnings.learnings_comments.csm_comment"
                      :rows="2"
                      placeholder="Add CSM comment..." />
                  </div>
                  <div class="space-y-2">
                    <label class="text-xs font-medium text-muted-foreground">TM</label>
                    <Textarea
                      v-model="learnings.learnings_comments.tm_comment"
                      :rows="2"
                      placeholder="Add TM comment..." />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </div>
  </div>
</template>
